import { ROUTES } from '@/utils/constants';
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import profileMockup from '@/assets/images/profile-mockup.png';

// Colors sourced from the hero art grid (CELLS in LandingHero)
const GRID_COLORS = {
  coral: '#8c4238',
  amber: '#886020',
  indigo: '#524898',
} as const;

const SECTIONS: {
  eyebrow?: string;
  title: string;
  desc?: string;
  cta: string;
  ctaColor: string;
  visual: 'releases' | 'events' | 'platforms';
  flip: boolean;
}[] = [
  {
    title: 'Your work, always on show',
    desc: "Tracks, videos, films, photo series, collections, whatever you make. Pin everything with a status so your audience always knows what's out and what's dropping next.",
    cta: 'Start showcasing',
    ctaColor: GRID_COLORS.coral,
    visual: 'releases',
    flip: false,
  },
  {
    title: 'Shows, screenings, pop-ups, all in one spot',
    desc: 'Add any event with a direct ticket or RSVP link. Your audience gets straight to the buy page in one tap.',
    cta: 'Add your events',
    ctaColor: GRID_COLORS.amber,
    visual: 'events',
    flip: true,
  },
  {
    title: 'Every platform, one link away',
    desc: 'Spotify, YouTube, Patreon, Behance, Twitch. Wherever you publish, your Nestra page ties it all together.',
    cta: 'Connect your platforms',
    ctaColor: GRID_COLORS.indigo,
    visual: 'platforms',
    flip: false,
  },
];

const RELEASE_CARDS = [
  {
    title: 'Glow Series',
    sub: 'Photo Prints · 12 pieces',
    badge: 'UPCOMING',
    variant: 'upcoming' as const,
    color: GRID_COLORS.coral,
  },
  {
    title: 'After Hours',
    sub: 'Short Film · 34 min',
    badge: 'OCT 2026',
    variant: 'date' as const,
    color: GRID_COLORS.amber,
  },
  {
    title: 'Drift',
    sub: 'EP · 6 tracks',
    badge: 'OUT NOW',
    variant: 'out' as const,
    color: GRID_COLORS.indigo,
  },
] as const;

function ArtCircles({
  color,
  className = 'lf-rs__art',
}: {
  color: string;
  className?: string;
}) {
  return (
    <svg
      className={className}
      viewBox="0 0 280 110"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden
    >
      <rect width="280" height="110" fill={color} />
      <circle cx="245" cy="-5" r="88" fill="white" fillOpacity="0.08" />
      <circle cx="45" cy="98" r="62" fill="black" fillOpacity="0.1" />
      <circle cx="188" cy="68" r="28" fill="white" fillOpacity="0.13" />
      <circle cx="140" cy="160" r="88" fill="white" fillOpacity="0.04" />
    </svg>
  );
}

function ArtBars({
  color,
  className = 'lf-rs__art',
}: {
  color: string;
  className?: string;
}) {
  return (
    <svg
      className={className}
      viewBox="0 0 280 110"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden
    >
      <rect width="280" height="110" fill={color} />
      <g transform="rotate(-13, 140, 55)">
        <rect
          x="-80"
          y="8"
          width="440"
          height="22"
          fill="white"
          fillOpacity="0.08"
        />
        <rect
          x="-80"
          y="38"
          width="440"
          height="14"
          fill="white"
          fillOpacity="0.06"
        />
        <rect
          x="-80"
          y="60"
          width="440"
          height="18"
          fill="white"
          fillOpacity="0.05"
        />
        <rect
          x="-80"
          y="86"
          width="440"
          height="10"
          fill="white"
          fillOpacity="0.04"
        />
      </g>
    </svg>
  );
}

function ArtWaves({
  color,
  className = 'lf-rs__art',
}: {
  color: string;
  className?: string;
}) {
  return (
    <svg
      className={className}
      viewBox="0 0 280 110"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden
    >
      <rect width="280" height="110" fill={color} />
      <circle
        cx="0"
        cy="110"
        r="60"
        fill="none"
        stroke="white"
        strokeOpacity="0.12"
        strokeWidth="1.5"
      />
      <circle
        cx="0"
        cy="110"
        r="100"
        fill="none"
        stroke="white"
        strokeOpacity="0.09"
        strokeWidth="1.5"
      />
      <circle
        cx="0"
        cy="110"
        r="140"
        fill="none"
        stroke="white"
        strokeOpacity="0.07"
        strokeWidth="1.5"
      />
      <circle
        cx="0"
        cy="110"
        r="180"
        fill="none"
        stroke="white"
        strokeOpacity="0.05"
        strokeWidth="1.5"
      />
      <circle
        cx="0"
        cy="110"
        r="220"
        fill="none"
        stroke="white"
        strokeOpacity="0.04"
        strokeWidth="1.5"
      />
    </svg>
  );
}

const RELEASE_ART_COMPONENTS = [ArtCircles, ArtBars, ArtWaves];

function ArtEventLines({
  color,
  className = 'lf-rs__art',
}: {
  color: string;
  className?: string;
}) {
  return (
    <svg
      className={className}
      viewBox="0 0 280 110"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden
    >
      <rect width="280" height="110" fill={color} />
      <path
        fill="none"
        stroke="white"
        strokeWidth="1"
        strokeOpacity="0.18"
        d="M-110,110 L0,0 M-88,110 L22,0 M-66,110 L44,0 M-44,110 L66,0 M-22,110 L88,0 M0,110 L110,0 M22,110 L132,0 M44,110 L154,0 M66,110 L176,0 M88,110 L198,0 M110,110 L220,0 M132,110 L242,0 M154,110 L264,0 M176,110 L286,0 M198,110 L308,0 M220,110 L330,0 M242,110 L352,0 M264,110 L374,0"
      />
    </svg>
  );
}

function ArtEventDots({
  color,
  className = 'lf-rs__art',
}: {
  color: string;
  className?: string;
}) {
  return (
    <svg
      className={className}
      viewBox="0 0 280 110"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden
    >
      <rect width="280" height="110" fill={color} />
      {([16, 48, 80, 112, 144, 176, 208, 240, 272] as const).map((x) => (
        <circle key={x} cx={x} cy={14} r="3" fill="white" fillOpacity="0.18" />
      ))}
      {([32, 64, 96, 128, 160, 192, 224, 256] as const).map((x) => (
        <circle key={x} cx={x} cy={42} r="3" fill="white" fillOpacity="0.13" />
      ))}
      {([16, 48, 80, 112, 144, 176, 208, 240, 272] as const).map((x) => (
        <circle key={x} cx={x} cy={70} r="3" fill="white" fillOpacity="0.09" />
      ))}
      {([32, 64, 96, 128, 160, 192, 224, 256] as const).map((x) => (
        <circle key={x} cx={x} cy={98} r="3" fill="white" fillOpacity="0.05" />
      ))}
    </svg>
  );
}

function ArtEventDiamonds({
  color,
  className = 'lf-rs__art',
}: {
  color: string;
  className?: string;
}) {
  return (
    <svg
      className={className}
      viewBox="0 0 280 110"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden
    >
      <rect width="280" height="110" fill={color} />
      <polygon
        points="280,-60 340,0 280,60 220,0"
        fill="none"
        stroke="white"
        strokeOpacity="0.13"
        strokeWidth="1.5"
      />
      <polygon
        points="280,-100 380,0 280,100 180,0"
        fill="none"
        stroke="white"
        strokeOpacity="0.10"
        strokeWidth="1.5"
      />
      <polygon
        points="280,-140 420,0 280,140 140,0"
        fill="none"
        stroke="white"
        strokeOpacity="0.07"
        strokeWidth="1.5"
      />
      <polygon
        points="280,-180 460,0 280,180 100,0"
        fill="none"
        stroke="white"
        strokeOpacity="0.05"
        strokeWidth="1.5"
      />
      <polygon
        points="280,-220 500,0 280,220 60,0"
        fill="none"
        stroke="white"
        strokeOpacity="0.03"
        strokeWidth="1.5"
      />
    </svg>
  );
}

function ReleasesVisual() {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    let intervalId: ReturnType<typeof setInterval> | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    function cycle() {
      el!.classList.add('is-open');
      timeoutId = setTimeout(() => el!.classList.remove('is-open'), 1500);
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          timeoutId = setTimeout(() => {
            cycle();
            intervalId = setInterval(cycle, 5000);
          }, 1000);
        } else {
          if (intervalId) clearInterval(intervalId);
          if (timeoutId) clearTimeout(timeoutId);
          el.classList.remove('is-open');
        }
      },
      { threshold: 0.5 },
    );

    observer.observe(el);
    return () => {
      observer.disconnect();
      if (intervalId) clearInterval(intervalId);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  return (
    <div className="lf-rs" ref={containerRef} aria-hidden="true">
      {RELEASE_CARDS.map(({ title, sub, badge, variant, color }, i) => {
        const ArtComponent = RELEASE_ART_COMPONENTS[i];
        return (
          <div key={title} className={`lf-rs__card lf-rs__card--${i + 1}`}>
            <ArtComponent color={color} />
            <div className="lf-rs__bottom">
              <div className="lf-rs__meta">
                <span className="lf-rs__title">{title}</span>
                <span className="lf-rs__sub">{sub}</span>
              </div>
              <span className={`lf-rs__badge lf-rs__badge--${variant}`}>
                {badge}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function EventsVisual() {
  const items = [
    {
      month: 'JUN',
      day: '28',
      venue: 'The Lyric',
      city: 'Chicago, IL',
      color: GRID_COLORS.coral,
      Art: ArtEventLines,
    },
    {
      month: 'JUL',
      day: '12',
      venue: 'Union Stage',
      city: 'Washington, D.C.',
      color: GRID_COLORS.amber,
      Art: ArtEventDots,
    },
    {
      month: 'AUG',
      day: '3',
      venue: 'Rough Trade',
      city: 'New York, NY',
      color: GRID_COLORS.indigo,
      Art: ArtEventDiamonds,
    },
  ];
  return (
    <div className="lf-visual lf-visual--events">
      {items.map(({ month, day, venue, city, color, Art }) => (
        <div key={month + day} className="lf-event-row lf-event-row--art">
          <Art color={color} className="lf-event-bg" />
          <div className="lf-event-date">
            <span className="lf-event-date__day">{day}</span>
            <span className="lf-event-date__month">{month}</span>
          </div>
          <div className="lf-event-text">
            <span className="lf-event-venue">{venue}</span>
            <span className="lf-event-city">{city}</span>
          </div>
          <div className="lf-ticket-btn">Tickets</div>
        </div>
      ))}
    </div>
  );
}

function PlatformsVisual() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => el.classList.toggle('is-floating', entry.isIntersecting),
      { threshold: 0.3 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="lf-visual lf-visual--mockup">
      <div className="lf-browser" ref={ref}>
        <div className="lf-browser__bar">
          <span className="lf-browser__dots">
            <i />
            <i />
            <i />
          </span>
          <span className="lf-browser__url">nestra.link/dave</span>
        </div>
        <div className="lf-browser__screen">
          <img
            src={profileMockup}
            alt="Nestra profile page"
            className="lf-mockup-img"
          />
        </div>
      </div>
    </div>
  );
}

const VISUALS = {
  releases: <ReleasesVisual />,
  events: <EventsVisual />,
  platforms: <PlatformsVisual />,
};

export function LandingFeatures() {
  return (
    <div className="landing-features">
      {SECTIONS.map(({ eyebrow, title, desc, cta, ctaColor, visual, flip }) => (
        <section
          key={visual}
          className={`landing-feature-section${flip ? ' landing-feature-section--flip' : ''}`}
        >
          <div className="landing-wide landing-feature-section__inner">
            <div className="landing-feature-section__text">
              {eyebrow && (
                <p
                  className="landing-feature-section__eyebrow"
                  style={{ color: ctaColor }}
                >
                  {eyebrow}
                </p>
              )}
              <h2 className="landing-feature-section__title">{title}</h2>
              {desc && <p className="landing-feature-section__desc">{desc}</p>}
              <Link
                to={ROUTES.SIGNUP}
                className="landing-feature-section__cta"
                style={{ background: ctaColor }}
              >
                {cta}
              </Link>
            </div>
            <div className="landing-feature-section__visual">
              {VISUALS[visual]}
            </div>
          </div>
        </section>
      ))}
    </div>
  );
}
